/**
 * AffiliateGiftBanner
 * -------------------
 * Shown on /download-app ABOVE the platform tiles when the visitor has
 * a valid `levanta_aff` cookie (set by useAffiliateCookie when they
 * landed via `?ref=CODE`).
 *
 * Flow on first render:
 *   1. Read cookie. Bail silently if absent.
 *   2. Respect localStorage `levanta_aff_dismissed` — user already
 *      clicked × on a previous visit.
 *   3. Respect localStorage `levanta_aff_seen_at` — hide if >24h old.
 *   4. Fetch /api/affiliate/info/{code}. Bail silently if invalid.
 *   5. Render the gift card. Tapping the code copies it via
 *      navigator.clipboard, with a 2s "Code copied!" swap.
 *   6. Stamp `levanta_aff_seen_at` on first successful render so the
 *      24-hour clock starts now.
 *
 * Design: warm amber/gold card (gift reveal tone) that stands apart
 * from the site's blue palette without clashing. Mobile-first, full
 * width on <sm, centered max-w-2xl on desktop, respects iPhone notch.
 */
import React, { useEffect, useState } from 'react';
import { Gift, Copy, Check, X } from 'lucide-react';
import { readAffiliateCookie } from '../hooks/useAffiliateCookie';

const SEEN_KEY = 'levanta_aff_seen_at';
const DISMISSED_KEY = 'levanta_aff_dismissed';
const SEEN_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AffiliateGiftBanner = () => {
  const [info, setInfo] = useState(null); // { code, affiliate_name, discount_pct }
  const [copied, setCopied] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISSED_KEY) === 'true') return;

    const code = readAffiliateCookie();
    if (!code) return;

    const seenAtRaw = localStorage.getItem(SEEN_KEY);
    if (seenAtRaw) {
      const seenAt = parseInt(seenAtRaw, 10);
      if (!Number.isNaN(seenAt) && Date.now() - seenAt > SEEN_TTL_MS) return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/affiliate/info/${encodeURIComponent(code)}`,
          { headers: { Accept: 'application/json' } },
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!data?.valid || cancelled) return;
        setInfo({
          code: data.code,
          affiliate_name: data.affiliate_name || 'A Levanta friend',
          discount_pct: data.discount_pct ?? 10,
        });
        // Start the 24h clock on the first successful surface.
        if (!seenAtRaw) {
          localStorage.setItem(SEEN_KEY, String(Date.now()));
        }
      } catch {
        // Network blip: silently skip. Not worth bothering the user.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCopy = async () => {
    if (!info?.code) return;
    try {
      await navigator.clipboard.writeText(info.code);
    } catch {
      // Older browsers / file:// — fall back to execCommand.
      const ta = document.createElement('textarea');
      ta.value = info.code;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  if (!info || dismissed) return null;

  return (
    <section
      className="px-6 pt-8 md:pt-10"
      data-testid="affiliate-gift-banner"
      aria-label="Referral code from a friend"
    >
      <div className="mx-auto max-w-2xl">
        <div className="relative rounded-3xl bg-gradient-to-br from-amber-50 via-amber-50 to-amber-100 ring-1 ring-amber-200/70 shadow-lg overflow-hidden">
          {/* subtle warm highlight */}
          <div
            aria-hidden="true"
            className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-amber-200/40 blur-2xl pointer-events-none"
          />

          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss this referral banner"
            data-testid="affiliate-gift-dismiss"
            className="absolute top-3 right-3 md:top-4 md:right-4 w-9 h-9 rounded-full bg-white/70 hover:bg-white text-[#0b1f3a]/55 hover:text-[#0b1f3a] flex items-center justify-center ring-1 ring-amber-200 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 z-10"
          >
            <X className="w-4 h-4" strokeWidth={2.5} />
          </button>

          <div className="relative px-5 py-6 md:px-8 md:py-7 pr-12 md:pr-16">
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-11 h-11 md:w-12 md:h-12 rounded-2xl bg-amber-500/15 flex items-center justify-center">
                <Gift className="w-6 h-6 text-amber-700" strokeWidth={2.2} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700">
                  A gift, from a friend
                </p>
                <h2
                  className="mt-0.5 text-xl md:text-2xl font-black text-[#0b1f3a] leading-snug"
                  data-testid="affiliate-gift-headline"
                >
                  Your friend gave you{' '}
                  <span className="text-amber-700">{info.discount_pct}% off</span>
                </h2>
              </div>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <p className="text-sm text-[#0b1f3a]/75">
                Use this code during checkout in the app:
              </p>
              <button
                type="button"
                onClick={handleCopy}
                aria-label={`Copy referral code ${info.code}`}
                data-testid="affiliate-gift-copy"
                className="group relative inline-flex items-center gap-3 self-start sm:self-auto px-4 py-3 rounded-2xl bg-white ring-1 ring-amber-300 hover:ring-amber-400 hover:shadow-md active:scale-[0.99] transition-all focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <span
                  className="font-mono text-lg md:text-xl font-bold tracking-[0.15em] text-[#0b1f3a] select-all"
                  data-testid="affiliate-gift-code"
                >
                  {info.code}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 group-hover:text-amber-800">
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5" strokeWidth={2.8} />
                      Code copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" strokeWidth={2.5} />
                      Tap to copy
                    </>
                  )}
                </span>
              </button>
            </div>

            <p
              className="mt-5 text-xs md:text-[13px] text-[#0b1f3a]/55"
              data-testid="affiliate-gift-sender"
            >
              Sent by {info.affiliate_name}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AffiliateGiftBanner;
